import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ActionConfigBuilder } from '@/components/ui/ActionConfigBuilder';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import type { ProjectAutomation } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

type AutomationAction = { id: number; actionType: string; sortOrder: number };

const TRIGGERS = ['TaskAssigned', 'TaskCompleted', 'SprintStarted', 'MilestoneCompleted', 'ProjectCompleted'];
const ACTION_TYPES = ['ASSIGN_TASK', 'NOTIFY', 'UPDATE_STATUS', 'CREATE_ISSUE'];

export function ProjectsAutomationPage() {
  const [rules, setRules] = useState<ProjectAutomation[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [actions, setActions] = useState<AutomationAction[]>([]);
  const [open, setOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [form, setForm] = useState({ workflowName: '', triggerEvent: 'TaskAssigned' });
  const [actionForm, setActionForm] = useState({ actionType: 'NOTIFY', actionConfigJson: '', sortOrder: 1 });

  const load = () => {
    projectsApi.listAutomations().then(setRules).catch((e) => showApiError(e, 'Failed to load automations'));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setActions([]);
      return;
    }
    projectsApi
      .listAutomationActions(selectedId)
      .then(setActions)
      .catch((e) => showApiError(e, 'Failed to load actions'));
  }, [selectedId]);

  const create = async () => {
    try {
      const created = await projectsApi.createAutomation(form);
      notify.success('Automation created');
      setOpen(false);
      load();
      setSelectedId(created.id);
    } catch (err) {
      showApiError(err, 'Create failed');
    }
  };

  const addAction = async () => {
    if (!selectedId) return;
    try {
      await projectsApi.addAutomationAction(selectedId, actionForm);
      notify.success('Action added');
      setActionOpen(false);
      setActions(await projectsApi.listAutomationActions(selectedId));
    } catch (err) {
      showApiError(err, 'Add action failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Automation"
        description="Workflow triggers and actions for tasks and sprints"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Projects', to: '/projects' },
          { label: 'Automation' },
        ]}
        actions={
          <Button onClick={() => setOpen(true)}>
            <AddIcon fontSize="small" /> New rule
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card divide-y">
          {rules.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`w-full p-4 flex justify-between text-left hover:bg-slate-50 ${selectedId === r.id ? 'bg-primary-50' : ''}`}
              onClick={() => setSelectedId(r.id)}
            >
              <div>
                <p className="font-medium">{r.workflowName}</p>
                <p className="text-sm text-slate-500">Trigger: {r.triggerEvent}</p>
              </div>
              <span className="text-xs">{r.active ? 'Active' : 'Inactive'}</span>
            </button>
          ))}
          {!rules.length && <p className="p-4 text-sm text-slate-500">No automation rules yet.</p>}
        </div>

        <div className="card p-4">
          {selectedId ? (
            <>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-sm">Workflow actions</h3>
                <Button variant="secondary" className="text-xs" onClick={() => setActionOpen(true)}>
                  Add action
                </Button>
              </div>
              <ul className="divide-y text-sm">
                {actions.map((a) => (
                  <li key={a.id} className="py-2 flex justify-between">
                    <span>{a.actionType}</span>
                    <span className="text-slate-500">Order {a.sortOrder}</span>
                  </li>
                ))}
                {!actions.length && <li className="py-2 text-slate-500">No actions — add at least one.</li>}
              </ul>
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a rule to manage actions.</p>
          )}
        </div>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create automation"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void create()}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Rule name" value={form.workflowName} onChange={(e) => setForm({ ...form, workflowName: e.target.value })} />
          <Select
            label="Trigger event"
            value={form.triggerEvent}
            onChange={(v) => setForm({ ...form, triggerEvent: v })}
            options={TRIGGERS.map((t) => ({ value: t, label: t }))}
          />
        </div>
      </Modal>

      <Modal
        open={actionOpen}
        onClose={() => setActionOpen(false)}
        title="Add workflow action"
        footer={
          <>
            <Button variant="secondary" onClick={() => setActionOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void addAction()}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Select
            label="Action type"
            value={actionForm.actionType}
            onChange={(v) => setActionForm({ ...actionForm, actionType: v, actionConfigJson: '' })}
            options={ACTION_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <ActionConfigBuilder
            actionType={actionForm.actionType}
            value={actionForm.actionConfigJson || '{}'}
            onChange={(json) => setActionForm({ ...actionForm, actionConfigJson: json })}
          />
          <Input
            label="Sort order"
            type="number"
            value={actionForm.sortOrder}
            onChange={(e) => setActionForm({ ...actionForm, sortOrder: Number(e.target.value) })}
          />
        </div>
      </Modal>
    </div>
  );
}
